import { _axios } from '@/plugins/axios';

const sacResource = '/api/v1/sac';

export default {
  all: () => _axios.get(`${sacResource}/all`),
  getBySocVersionId: socVersionId => _axios.get(`${sacResource}/soc-version/${socVersionId}`),
  create: sac => _axios.post(`${sacResource}/create`, sac),
  delete: id => _axios.delete(`${sacResource}/delete/${id}`),
  update: sac => _axios.put(`${sacResource}/update`, sac),
  getScores: () => _axios.get(`${sacResource}/scores`),
  getCaeTemplate: requirementId =>
    _axios.get(`${sacResource}/sac-composer/cae-json-template/${requirementId}`),
  getRequirementIds: () => _axios.get(`${sacResource}/sac-composer/requirement-ids`),
  composeSac: (requirementId, json) => {
    // Ensure the JSON has the correct Claims structure
    let payload = json;
    if (!json.Claims && Array.isArray(json)) {
      payload = { Claims: json };
    }

    return _axios.post(`${sacResource}/compose-sac/${requirementId}`, payload);
  },
  checkDuplicateName: name =>
    _axios.get(`${sacResource}/check-duplicate-name/${encodeURIComponent(name)}`),

  // ── SAC Composer Chat ────────────────────────────────────────────

  /**
   * Send a message to the SAC Composer.
   * @param {string} threadId - Thread identifier (generated if empty)
   * @param {string} message  - The human message
   * @param {object} options  - Optional: { standardName, updateContextMode, contextOfCertification }
   */
  chatWithSacComposer: (threadId, message, options = {}) => {
    if (!message || message.trim().length === 0) {
      return Promise.reject(new Error('Message cannot be empty'));
    }

    const params = {
      thread_id: threadId || '',
      message: message.trim(),
    };

    if (options.standardName) {
      params.standard_name = options.standardName;
    }
    if (options.updateContextMode) {
      params.update_context_mode = options.updateContextMode;
    }

    // Build request body (only include fields that are set)
    const body = {};
    if (options.contextOfCertification) {
      body.context_of_certification = options.contextOfCertification;
    }

    return _axios.post(
      `${sacResource}/sac-composer/chat`,
      Object.keys(body).length > 0 ? body : null,
      {
        params,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        timeout: 300000,
      },
    );
  },

  /**
   * Stream a message to the SAC Composer via SSE.
   * Returns the raw response so the caller can read the event stream.
   */
  streamChatWithSacComposer: (threadId, message, options = {}) => {
    if (!message || message.trim().length === 0) {
      return Promise.reject(new Error('Message cannot be empty'));
    }

    const params = new URLSearchParams({
      thread_id: threadId || '',
      message: message.trim(),
    });
    if (options.standardName) params.set('standard_name', options.standardName);
    if (options.updateContextMode) params.set('update_context_mode', options.updateContextMode);

    const body = {};
    if (options.contextOfCertification) {
      body.context_of_certification = options.contextOfCertification;
    }

    // Use fetch for SSE since axios doesn't support streaming natively
    const token = localStorage.token;
    return fetch(
      `${
        process.env.VUE_APP_AXIOSBASE
      }${sacResource}/sac-composer/chat/stream?${params.toString()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : null,
      },
    );
  },

  /**
   * Submit HITL (Human-in-the-Loop) decisions.
   * @param {string} threadId - Thread identifier
   * @param {Array}  decisions - Array of { type: 'approve'|'reject', explanation?: string }
   * @param {string} standardName - Optional standard name
   */
  hitlConfirm: (threadId, decisions, standardName = null) => {
    const params = { thread_id: threadId };
    if (standardName) params.standard_name = standardName;

    return _axios.post(
      `${sacResource}/sac-composer/hitl-confirm`,
      { decisions },
      { params, timeout: 300000 },
    );
  },

  // ── SAC Composer Session Management ──────────────────────────────

  /** Get paginated list of threads for the current user. */
  getUserThreads: (limit = null, offset = 0) => {
    const params = { offset };
    if (limit) params.limit = limit;
    return _axios.get(`${sacResource}/sac-composer/threads`, { params });
  },

  /** Get session state for a specific thread. */
  getThreadState: threadId =>
    _axios.get(`${sacResource}/sac-composer/session/state`, {
      params: { thread_id: threadId },
    }),

  /** Get SAC data for a specific thread. */
  getSessionSac: threadId =>
    _axios.get(`${sacResource}/sac-composer/session/sac`, {
      params: { thread_id: threadId },
    }),

  /** Get canonical initial state for new sessions. */
  getInitialState: () => _axios.get(`${sacResource}/sac-composer/initial-state`),

  /** Delete a thread. */
  deleteThread: threadId =>
    _axios.delete(`${sacResource}/sac-composer/thread`, {
      params: { thread_id: threadId, confirm: true },
    }),

  // ── SAC Composer Save/Persist ────────────────────────────────────

  saveClaim: (requirementId, claimData) => {
    const payload = { requirementId, claim: claimData };
    return _axios.post(`${sacResource}/sac-composer/save-claim`, payload);
  },

  saveAllClaims: sacData => _axios.post(`${sacResource}/sac-composer/save-all-claims`, sacData),

  saveExclaimScores: scoredJson => {
    // Ensure we're sending the raw JSON string, not the parsed object
    const jsonString = typeof scoredJson === 'string' ? scoredJson : JSON.stringify(scoredJson);

    return _axios.post(`${sacResource}/save-exclaim-scores`, jsonString, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
};
